import React, { useCallback, useEffect, useState } from 'react';
import PageHeader from '../../components/PageHeader';
import { apiGet } from '../../lib/api';
import { AlertCircle, AlertTriangle, Info, ChevronDown, ChevronRight, Loader2, MapPin, RefreshCw, ShieldAlert } from 'lucide-react';

const SEVERITY_META = {
  high:   { color: 'bg-red-50 border-red-200 text-red-800',     dot: 'bg-red-500',     icon: AlertCircle,    label: 'High' },
  medium: { color: 'bg-amber-50 border-amber-200 text-amber-800', dot: 'bg-amber-500', icon: AlertTriangle, label: 'Medium' },
  low:    { color: 'bg-teal-50 border-teal-200 text-teal-800',     dot: 'bg-teal-500',     icon: Info,           label: 'Low' },
};

const CODE_META = {
  rapid_toggle:      { label: 'Rapid toggle', emoji: '⚡' },
  long_session:      { label: 'Long session', emoji: '⏱️' },
  excessive_total:   { label: 'Excessive total', emoji: '🚨' },
  unclosed_session:  { label: 'Open past midnight', emoji: '🌙' },
  no_geo:            { label: 'No GPS', emoji: '📍' },
  excessive_toggles: { label: 'Too many toggles', emoji: '🔁' },
};

function defaultMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function formatDuration(mins) {
  if (mins == null) return '—';
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}

export default function AttendanceAnomalies() {
  const [month, setMonth] = useState(defaultMonth());
  const [severity, setSeverity] = useState('');
  const [code, setCode] = useState('');
  const [department, setDepartment] = useState('');
  const [data, setData] = useState({ rows: [], total_anomalies: 0, total_employees_with_anomalies: 0, by_code: {}, by_severity: {} });
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});

  useEffect(() => {
    // Pull departments once
    apiGet('/hr/employees').then((res) => {
      const list = Array.isArray(res) ? res : (res?.rows || []);
      const depts = Array.from(new Set(list.map((e) => e.department).filter(Boolean))).sort();
      setDepartments(depts);
    }).catch(() => {});
  }, []);

  const load = useCallback(() => {
    setLoading(true);
    const params = new URLSearchParams({ month });
    if (severity) params.set('severity', severity);
    if (code) params.set('code', code);
    if (department) params.set('department', department);
    apiGet(`/hr/attendance/anomalies?${params.toString()}`)
      .then(setData)
      .finally(() => setLoading(false));
  }, [month, severity, code, department]);

  useEffect(() => { load(); }, [load]);

  const toggleRow = (key) => setExpanded((e) => ({ ...e, [key]: !e[key] }));

  return (
    <div className="p-6 md:p-8 space-y-5" data-testid="anomalies-page">
      <PageHeader
        overline="People & HR · Compliance"
        title="Attendance Anomalies"
        subtitle="Automated checks for suspicious patterns — rapid toggles, long sessions, midnight crossings, missing GPS and more."
      />

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <KPI label="Employees Flagged" value={data.total_employees_with_anomalies} tone="default" />
        <KPI label="Total Anomalies" value={data.total_anomalies} tone="default" />
        <KPI label="High Severity" value={data.by_severity?.high || 0} tone="red" />
        <KPI label="Medium Severity" value={data.by_severity?.medium || 0} tone="amber" />
      </div>

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-sm p-4 flex flex-wrap items-end gap-3">
        <Field label="Month">
          <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="border border-slate-300 rounded-sm px-2.5 py-1.5 text-sm" data-testid="filter-month" />
        </Field>
        <Field label="Department">
          <select value={department} onChange={(e) => setDepartment(e.target.value)} className="border border-slate-300 rounded-sm px-2.5 py-1.5 text-sm bg-white min-w-[160px]" data-testid="filter-dept">
            <option value="">All Departments</option>
            {departments.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
        </Field>
        <Field label="Severity">
          <select value={severity} onChange={(e) => setSeverity(e.target.value)} className="border border-slate-300 rounded-sm px-2.5 py-1.5 text-sm bg-white" data-testid="filter-severity">
            <option value="">All</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
        </Field>
        <Field label="Anomaly Type">
          <select value={code} onChange={(e) => setCode(e.target.value)} className="border border-slate-300 rounded-sm px-2.5 py-1.5 text-sm bg-white min-w-[160px]" data-testid="filter-code">
            <option value="">All Types</option>
            {Object.entries(CODE_META).map(([k, m]) => <option key={k} value={k}>{m.emoji} {m.label}</option>)}
          </select>
        </Field>
        <button onClick={() => { setSeverity(''); setCode(''); setDepartment(''); }} className="text-xs px-3 py-1.5 border border-slate-300 rounded-sm hover:bg-slate-50 font-display font-bold uppercase tracking-wider">Clear</button>
        <button onClick={load} className="ml-auto text-xs px-3 py-1.5 border border-[#1F7A6E] text-[#1F7A6E] rounded-sm hover:bg-teal-50 flex items-center gap-1 font-display font-bold uppercase tracking-wider" data-testid="refresh-anomalies">
          <RefreshCw size={11} /> Refresh
        </button>
      </div>

      {/* Anomaly type chips */}
      {Object.keys(data.by_code || {}).length > 0 && (
        <div className="bg-white border border-slate-200 rounded-sm p-3 flex flex-wrap items-center gap-2 text-xs">
          <span className="text-[10px] font-display font-bold uppercase tracking-wider text-slate-500 mr-1">Breakdown:</span>
          {Object.entries(data.by_code).map(([k, c]) => (
            <button
              key={k}
              onClick={() => setCode(code === k ? '' : k)}
              className={`px-2 py-1 rounded-sm border text-xs flex items-center gap-1 font-display font-semibold ${code === k ? 'bg-[#1F7A6E] text-white border-[#1F7A6E]' : 'bg-slate-50 text-slate-700 border-slate-200 hover:border-slate-400'}`}
            >
              <span>{CODE_META[k]?.emoji || '•'}</span> {CODE_META[k]?.label || k} <span className="ml-1 font-bold">{c}</span>
            </button>
          ))}
        </div>
      )}

      {/* Rows */}
      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        {loading ? (
          <div className="py-12 text-center text-sm text-slate-500"><Loader2 size={14} className="animate-spin inline mr-2" /> Scanning attendance records…</div>
        ) : data.rows.length === 0 ? (
          <div className="py-12 text-center text-sm text-slate-500 flex flex-col items-center gap-2">
            <ShieldAlert size={28} className="text-emerald-500" />
            <div className="font-display font-bold text-base text-slate-700">No anomalies for the selected filters</div>
            <div>All attendance records pass the configured policy checks.</div>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 font-display">
              <tr>
                <th className="text-left py-2.5 px-4 w-10"></th>
                <th className="text-left py-2.5 px-4 w-28">Date</th>
                <th className="text-left py-2.5 px-4">Employee</th>
                <th className="text-left py-2.5 px-4">Department</th>
                <th className="text-center py-2.5 px-4 w-20">Sessions</th>
                <th className="text-right py-2.5 px-4 w-28">Total</th>
                <th className="text-left py-2.5 px-4">Anomalies</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => {
                const key = `${r.employee_id}-${r.date}`;
                const open = !!expanded[key];
                const topSev = r.anomalies.reduce((acc, a) => {
                  if (a.severity === 'high') return 'high';
                  if (a.severity === 'medium' && acc !== 'high') return 'medium';
                  return acc;
                }, 'low');
                const meta = SEVERITY_META[topSev];
                return (
                  <React.Fragment key={key}>
                    <tr className="border-t border-slate-100 hover:bg-slate-50/50 cursor-pointer" onClick={() => toggleRow(key)} data-testid={`anomaly-row-${key}`}>
                      <td className="py-2 px-4">{open ? <ChevronDown size={14} className="text-slate-400" /> : <ChevronRight size={14} className="text-slate-400" />}</td>
                      <td className="py-2 px-4 font-mono text-xs">{r.date}</td>
                      <td className="py-2 px-4">
                        <div className="flex items-center gap-2">
                          <span className={`w-1.5 h-1.5 rounded-full ${meta.dot}`} />
                          <div>
                            <div className="font-semibold">{r.name}</div>
                            <div className="text-[10px] text-slate-500 font-mono">{r.emp_id}</div>
                          </div>
                        </div>
                      </td>
                      <td className="py-2 px-4 text-xs text-slate-600">{r.department || '—'}</td>
                      <td className="py-2 px-4 text-center text-xs">{r.sessions_count}</td>
                      <td className="py-2 px-4 text-right text-xs font-display font-bold">{formatDuration(r.duration_mins)}</td>
                      <td className="py-2 px-4">
                        <div className="flex flex-wrap gap-1">
                          {r.anomalies.map((a, i) => {
                            const m = SEVERITY_META[a.severity];
                            const cm = CODE_META[a.code];
                            return (
                              <span key={i} className={`text-[10px] font-display font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm border ${m.color}`}>
                                {cm?.emoji} {cm?.label || a.code}
                              </span>
                            );
                          })}
                        </div>
                      </td>
                    </tr>
                    {open && (
                      <tr className="border-t border-slate-100 bg-slate-50/30">
                        <td></td>
                        <td colSpan={6} className="py-3 px-4">
                          <ul className="space-y-1.5">
                            {r.anomalies.map((a, i) => {
                              const m = SEVERITY_META[a.severity];
                              const Icon = m.icon;
                              return (
                                <li key={i} className={`p-2.5 border rounded-sm flex items-start gap-2 text-xs ${m.color}`}>
                                  <Icon size={14} className="mt-0.5 shrink-0" />
                                  <div className="flex-1 min-w-0">
                                    <div className="font-semibold flex items-center gap-2">
                                      <span className={`text-[9px] font-display font-bold uppercase tracking-widest px-1.5 py-0.5 rounded-sm bg-white/60`}>{m.label}</span>
                                      {a.label}
                                    </div>
                                    <div className="text-[11px] mt-0.5 opacity-80">{a.details}</div>
                                  </div>
                                </li>
                              );
                            })}
                          </ul>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      <div className="text-[10px] text-slate-500 flex items-center gap-1 flex-wrap">
        <Info size={11} /> Detection rules:
        rapid toggle (&lt; 5m), long session (&gt; 8h), excessive total (&gt; 14h), open past midnight (&gt; 2h), no GPS for the day, &gt; 6 toggles. <MapPin size={11} className="ml-2" /> All times IST.
      </div>
    </div>
  );
}

function KPI({ label, value, tone }) {
  const tones = {
    default: 'border-slate-200',
    amber: 'border-amber-200 bg-amber-50',
    red: 'border-red-200 bg-red-50',
  };
  return (
    <div className={`bg-white border rounded-sm p-4 ${tones[tone] || tones.default}`}>
      <div className="text-[10px] font-display font-bold uppercase tracking-[0.16em] text-slate-500">{label}</div>
      <div className="font-display font-extrabold text-3xl mt-1.5">{value || 0}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-wider font-display font-bold text-slate-500 block mb-1">{label}</span>
      {children}
    </label>
  );
}
